import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import SendIcon from '@mui/icons-material/Send';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import trainingApi from '../../services/trainingApi';

/**
 * WizardChatMessage - renders a single message in the wizard conversation.
 * Lecturer messages on the right (blue), wizard messages on the left (grey).
 */
const WizardChatMessage = ({ message }) => {
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 1.5,
      }}
    >
      <Box sx={{ maxWidth: '75%' }}>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.25, display: 'block' }}>
          {isUser ? 'You' : 'Wizard'}
        </Typography>
        <Box
          sx={{
            bgcolor: isUser ? 'primary.main' : 'grey.100',
            color: isUser ? 'primary.contrastText' : 'text.primary',
            borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * ScenarioPreviewPanel - shows the generated scenario config in the right panel.
 */
const ScenarioPreviewPanel = ({ config }) => {
  if (!config) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Generated Scenario</Typography>
      <Divider sx={{ mb: 2 }} />

      {config.name && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Name</Typography>
          <Typography variant="body1">{config.name}</Typography>
        </Box>
      )}

      {config.description && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Description</Typography>
          <Typography variant="body2">{config.description}</Typography>
        </Box>
      )}

      {config.category && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Category</Typography>
          <Chip label={config.category} size="small" sx={{ textTransform: 'capitalize' }} />
        </Box>
      )}

      {config.difficulty && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Difficulty</Typography>
          <Chip label={config.difficulty} size="small" sx={{ textTransform: 'capitalize' }} />
        </Box>
      )}

      {config.scenarioContext?.presentingComplaint && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Presenting Complaint</Typography>
          <Typography variant="body2">{config.scenarioContext.presentingComplaint}</Typography>
        </Box>
      )}

      {config.scenarioContext?.backgroundBrief && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Background</Typography>
          <Typography variant="body2">{config.scenarioContext.backgroundBrief}</Typography>
        </Box>
      )}

      {config.learningObjectives?.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Learning Objectives</Typography>
          <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
            {config.learningObjectives.map((obj, i) => (
              <li key={i}><Typography variant="body2">{obj}</Typography></li>
            ))}
          </ul>
        </Box>
      )}

      {config.patientProfile && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary">Patient Profile</Typography>
          <Typography variant="body2">
            {config.patientProfile.name || 'Unnamed'} — {config.patientProfile.presentingConcerns?.join(', ') || 'No concerns listed'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const ScenarioWizardPage = () => {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState(null);
  const [generatedConfig, setGeneratedConfig] = useState(null);
  const [appliedScenario, setAppliedScenario] = useState(null);
  const [serviceGroups, setServiceGroups] = useState([]);
  const [institutionGroupId, setInstitutionGroupId] = useState('');
  const [platformGroupId, setPlatformGroupId] = useState(null);
  const chatEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Groups this person can file the scenario in. Lecturers get the groups they teach
  // in (defaulting to theirs); admins get every group, defaulting to the Platform Library.
  useEffect(() => {
    let cancelled = false;
    trainingApi.getAuthorGroups()
      .then(response => {
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        setServiceGroups(Array.isArray(data.groups) ? data.groups : []);
        setPlatformGroupId(data.libraryGroupId || null);
        if (data.defaultGroupId) setInstitutionGroupId(data.defaultGroupId);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Start wizard session on mount
  useEffect(() => {
    let cancelled = false;
    const startSession = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await trainingApi.startWizardSession();
        if (cancelled) return;
        const body = response.data || response;
        const data = body.data || body;
        setSessionId(data.sessionId || data._id);
        const wizardText = data.text || data.message;
        if (wizardText) {
          setMessages([{ role: 'assistant', content: wizardText }]);
        } else if (data.messages?.length) {
          setMessages(data.messages);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[ScenarioWizard] Failed to start session:', err);
          setError(err.response?.data?.error || err.message || 'Failed to start wizard session');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    startSession();
    return () => { cancelled = true; };
  }, []);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || !sessionId || isSending) return;

    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: trimmed }]);
    setIsSending(true);
    setError(null);

    try {
      const response = await trainingApi.sendWizardMessage(sessionId, trimmed);
      const body = response.data || response;
      const data = body.data || body;

      const wizardText = data.text || data.message;
      if (wizardText) {
        setMessages(prev => [...prev, { role: 'assistant', content: wizardText }]);
      }

      // Check if the wizard produced a config
      if (data.configs || data.generatedConfig || data.config) {
        setGeneratedConfig(data.configs || data.generatedConfig || data.config);
      }
    } catch (err) {
      console.error('[ScenarioWizard] Send error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleApply = async () => {
    if (!sessionId || isApplying) return;
    setIsApplying(true);
    setError(null);

    try {
      const response = await trainingApi.applyWizardConfig(sessionId, {
        institutionGroupId: institutionGroupId || undefined,
      });
      const body = response.data || response;
      const data = body.data || body;
      setAppliedScenario(data.scenario || data);
    } catch (err) {
      console.error('[ScenarioWizard] Apply error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to apply configuration');
    } finally {
      setIsApplying(false);
    }
  };

  // Success state after applying
  if (appliedScenario) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>Scenario Created</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          "{appliedScenario.name || 'New Scenario'}" has been created successfully.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => navigate(`/training/scenarios/${appliedScenario._id}/edit`)}
          >
            Edit Scenario
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/training/scenarios')}
          >
            Back to Library
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 180px)', minHeight: 400 }}>
      {/* Left panel: Chat (70%) */}
      <Paper
        variant="outlined"
        sx={{
          flex: 7,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoFixHighIcon color="primary" />
            <Typography variant="h6">Scenario Wizard</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Describe the training scenario you want to create. The wizard will help shape it.
          </Typography>
        </Box>

        {/* Messages */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {messages.map((msg, idx) => (
            <WizardChatMessage key={idx} message={msg} />
          ))}

          {isSending && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
              <Box sx={{ bgcolor: 'grey.100', borderRadius: '18px 18px 18px 4px', px: 2, py: 1.5 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2" component="span" color="text.secondary">
                  Thinking...
                </Typography>
              </Box>
            </Box>
          )}

          <div ref={chatEndRef} />
        </Box>

        {/* Input */}
        <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              size="small"
              placeholder="Describe what you want..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!sessionId || isSending}
            />
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={!input.trim() || !sessionId || isSending}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Right panel: Preview (30%) */}
      <Paper
        variant="outlined"
        sx={{
          flex: 3,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        {generatedConfig ? (
          <>
            <ScenarioPreviewPanel config={generatedConfig} />
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', mt: 'auto' }}>
              {serviceGroups.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <InputLabel>Author for...</InputLabel>
                  <Select
                    value={institutionGroupId}
                    label="Author for..."
                    onChange={(e) => setInstitutionGroupId(e.target.value)}
                  >
                    {serviceGroups
                      .slice()
                      .sort((a, b) => {
                        if (a.groupType === 'platform_authoring') return -1;
                        if (b.groupType === 'platform_authoring') return 1;
                        return (a.name || '').localeCompare(b.name || '');
                      })
                      .map(g => (
                        <MenuItem key={g._id} value={g._id}>
                          {g.name}
                          {g.groupType === 'platform_authoring' && ' (Platform Library)'}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
              {platformGroupId && institutionGroupId === platformGroupId && (
                <Chip
                  label="Platform Library"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 1.5 }}
                />
              )}
              <Button
                variant="contained"
                color="success"
                fullWidth
                onClick={handleApply}
                disabled={isApplying}
                startIcon={isApplying ? <CircularProgress size={16} /> : <CheckCircleIcon />}
              >
                {isApplying ? 'Creating...' : 'Apply & Create Scenario'}
              </Button>
            </Box>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
            <AutoFixHighIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="body1" color="text.secondary" textAlign="center">
              The generated scenario will appear here as you chat with the wizard.
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default ScenarioWizardPage;
